// vendor libs
import escape from 'lodash/escape';
import Logging from 'app/common/lib/Logging';

const printStackTraceLoader = () =>
  import(/* webpackChunkName: "stacktraceLibrary" */ 'stacktrace-js');

const errorLog = Logging.getLogger('errorUtil');
const isJQueryEvent = (event) => Object.keys(event).some((key) => key.includes('jQuery'));

const errorUtil = {};

/**
 * Report a client error to the server.
 *
 * @param {Object} target The target of the error
 * @param {string} fnName The name of the wrapped function that caught the
 *     error.
 * @param {Array.<Object>} fnArguments The arguments of the wrapped function
 *     that caught the error.
 * @param {Error} error The caught error
 */
errorUtil.reportError = function(target, fnName, fnArguments, error) {
  printStackTraceLoader().then(({ default: printStackTrace }) => {
    const extraData = {
      fn_name: fnName || 'unknown_function_name',
    };

    if (target instanceof Object && target.getErrorData !== undefined) {
      Object.assign(extraData, target.getErrorData(fnName));
    }

    if (fnArguments[0] && isJQueryEvent(fnArguments[0])) {
      const event = fnArguments[0];
      const currentTarget = event.currentTarget;
      extraData.event = {
        type: event.type,
        target_class: currentTarget.className.replace(/\s+/g, ' '),
      };
      if (currentTarget.href) {
        extraData.event.target_href = currentTarget.href;
      }
    }

    extraData.traceback = printStackTrace({ e: error }).join('\n');
    errorLog.error(error.message, extraData);
  });
};

/** @type {?function(string, function()=)} */
errorUtil._showErrorCallback = errorUtil._showErrorCallback || null;

/**
 * @param {function(string, function()=)} callback
 */
errorUtil.setShowHtmlErrorCallback = function(callback) {
  errorUtil._showErrorCallback = callback;
};

/**
 * Helper function that just creates a ConfirmDialog
 * and renders the error passed along. This one happens
 * client-side making it a bit faster.
 *
 * @param {string} message The error message to display
 * @param {function()=} complete Called after the message is dismissed
 * @param {?string} heading Optional heading passed to the callback
 */
errorUtil.showError = function(message, complete, heading) {
  const htmlMessage = escape(message);
  return errorUtil.showHtmlError(htmlMessage, complete, heading);
};

/**
 * Helper function that creates a ConfirmDialog
 * and renders the error passed along however the
 * error message isn't escaped so the message can
 * contain HTML.
 *
 * @param {string} htmlMessage HTML-encoded error message
 * @param {function()=} complete Called after the message is dismissed
 */
errorUtil.showHtmlError = function(htmlMessage, complete, heading) {
  return errorUtil._showErrorCallback(htmlMessage, complete, heading);
};

/**
 * NoEmailProvidedError
 *
 * An error to describe when a user attempts to signup
 * from a social account without an email
 *
 * @constructor
 */
errorUtil.NoEmailProvidedError = function() {};
errorUtil.NoEmailProvidedError.prototype = new Error();

export default errorUtil;
